import React, { createContext, useContext, useState, useCallback } from 'react';
import Dialog from './Dialog';

const DialogContext = createContext();

export const DialogProvider = ({ children }) => {
  const [dialogs, setDialogs] = useState([]);

  const openDialog = useCallback(({
    title,
    content,
    onConfirm,
    confirmText = '확인',
    cancelText = '취소',
    type = 'info',
    showConfirm = true,
    showCancel = true
  }) => {
    return new Promise((resolve, reject) => {
      const id = Date.now() + Math.random();
      
      const newDialog = {
        id,
        title,
        content,
        confirmText,
        cancelText,
        type,
        showConfirm,
        showCancel,
        onConfirm: () => {
          if (onConfirm) {
            try {
              const result = onConfirm();
              if (result instanceof Promise) {
                result.then(resolve).catch(reject);
              } else {
                resolve(result);
              }
            } catch (error) {
              reject(error);
            }
          } else {
            resolve(true);
          }
          closeDialog(id);
        },
        onClose: () => {
          resolve(false);
          closeDialog(id);
        }
      };

      setDialogs(prev => [...prev, newDialog]);
    });
  }, []);

  const closeDialog = useCallback((id) => {
    setDialogs(prev => prev.filter(dialog => dialog.id !== id));
  }, []);

  // 간편한 호출 함수들
  const confirm = useCallback((title, content, options = {}) => {
    return openDialog({
      title,
      content,
      type: 'warning',
      ...options
    });
  }, [openDialog]);

  const alert = useCallback((title, content, options = {}) => {
    return openDialog({
      title,
      content,
      showCancel: false,
      ...options
    });
  }, [openDialog]);

  const contextValue = {
    openDialog,
    closeDialog,
    confirm,
    alert
  };

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
      {dialogs.map(dialog => (
        <Dialog
          key={dialog.id}
          isOpen={true}
          title={dialog.title}
          onClose={dialog.onClose}
          onConfirm={dialog.onConfirm}
          confirmText={dialog.confirmText}
          cancelText={dialog.cancelText}
          type={dialog.type}
          showConfirm={dialog.showConfirm}
          showCancel={dialog.showCancel}
        >
          {typeof dialog.content === 'string' ? (
            <p className="text-base-content">{dialog.content}</p>
          ) : (
            dialog.content
          )}
        </Dialog>
      ))}
    </DialogContext.Provider>
  );
};

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};
